import * as Yup from 'yup';

export enum AccountCondition {
    NET30 = 'net30',
    NET45 = 'net45',
    NET60 = 'net60'
}

export const accountInfoConditions = Object.keys(AccountCondition).map(key => AccountCondition[key]);

export enum AccountPaymentType {
    INTERNAL = 'internal',
    EXTERNAL = 'external'
}

export enum SellerType {
    INTERNAL = 'internal',
    SELF_SERVE = 'self_serve'
}

export const accountPaymentTypes = Object.keys(AccountPaymentType).map(key => AccountPaymentType[key]);

export enum TakeRateCapType {
    DISABLED = 'disabled',
    FROM_GROSS_SPEND = 'from_gross_spend',
    AFTER_EXPENSES = 'after_expenses'
}

export const takeRateCapTypeLabels: Record<TakeRateCapType, string> = {
    [TakeRateCapType.DISABLED]: 'Disabled',
    [TakeRateCapType.FROM_GROSS_SPEND]: 'From gross spend',
    [TakeRateCapType.AFTER_EXPENSES]: 'After expenses'
};

export const takeRateCapTypes = Object.values(TakeRateCapType);

export interface ITakeRateCap {
    type: TakeRateCapType;
    value: number;
}

export const defaultTakeRateCap: ITakeRateCap = {
    type: TakeRateCapType.DISABLED,
    value: 0
};

export interface IReleaseInterval {
    releaseId: string;
    interval: {
        start: string;
        end: string;
    }
    releaseDate: string;
    isReleased: boolean;
    status: string;
}

const releaseIntervalStatusLabels: Record<string, string> = {
    pending: 'Sent to Trolley',
    created: 'Current interval',
    payment_failed: 'Payment failed',
    payment_returned: 'Payment returned',
    payment_deleted: 'Payment deleted',
    recipient_not_found: 'Recipient not found',
    inactive_recipient: 'Inactive recipient',
    not_faced_threshold: 'Below threshold',
};

const humanizeReleaseIntervalStatus = (status: string): string => {
    const normalized = status.trim().replace(/_/g, ' ').replace(/\s+/g, ' ');
    if (!normalized) {
        return '—';
    }
    return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

export const getReleaseIntervalStatusLabel = (status: string): string => {
    if (!status) {
        return '—';
    }

    return releaseIntervalStatusLabels[status] ?? humanizeReleaseIntervalStatus(status);
};

export interface IAccountInfo {
    accountId: number;
    conditions: AccountCondition;
    paymentType: string;
    minPaymentsBalance: number;
    lastUnreleasedIntervals: IReleaseInterval[];
    recipientId: string;
    isActive: boolean;
    companyEmail: string;
}

export const AccountInfoSchema = Yup.object().shape<IAccountInfo>({
    accountId: Yup.number().nullable(),
    conditions: Yup.mixed<AccountCondition>().oneOf(Object.values(AccountCondition)).required(),
    paymentType:  Yup.mixed<AccountPaymentType>().oneOf(Object.values(AccountPaymentType)).required(),
    minPaymentsBalance: Yup.number().required(),
});

export const defaultAccountInfo: IAccountInfo = {
    accountId: null,
    conditions: AccountCondition.NET60,
    paymentType: 'internal',
    minPaymentsBalance: 1000,
    companyEmail: ''
};
